import { useEffect, useRef, useState } from "react";
import { Button, Form, Table } from "react-bootstrap";
import {
  listPowSchedule,
  createPowScheduleItem,
  updatePowScheduleItem,
  deletePowScheduleItem,
  reorderList,
} from "../../services/apiClient.js";
import { formatDate } from "../../utils/date.js";
import { SortableRows, SortableTr } from "../SortableRows.jsx";

// The "Schedule" checklist beside the POW calendar — an ordered to-do list of
// description + complete flag (PRE-START, SOW sent, …), separate from the dated
// calendar tasks. (Legacy GPSProgressToDo / ToDoList.ascx.)

const EMPTY = { description: "", is_complete: false };

function ClaimPowSchedule({ claimId }) {
  const [items, setItems] = useState([]);
  const [hideComplete, setHideComplete] = useState(false);
  const [editId, setEditId] = useState(null); // null | "new" | id
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const editRef = useRef(null);

  function load(hide = hideComplete) {
    return listPowSchedule(claimId, hide)
      .then(setItems)
      .catch(() => setError("Unable to load schedule."));
  }
  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  useEffect(() => {
    if (editId !== null) editRef.current?.focus();
  }, [editId]);

  const set = (patch) => setForm((c) => ({ ...c, ...patch }));

  function changeHide(e) {
    setHideComplete(e.target.checked);
    load(e.target.checked);
  }
  function startNew() {
    setError("");
    setForm(EMPTY);
    setEditId("new");
  }
  function startEdit(it) {
    setError("");
    setForm({ description: it.description || "", is_complete: !!it.is_complete });
    setEditId(it.id);
  }
  async function save() {
    if (!form.description.trim()) {
      setError("A description is required.");
      return;
    }
    setError("");
    try {
      if (editId === "new") await createPowScheduleItem(claimId, form);
      else await updatePowScheduleItem(claimId, editId, form);
      setEditId(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save item.");
    }
  }
  async function remove(it) {
    if (!window.confirm(`Delete "${(it.description || "").slice(0, 60)}"?`)) return;
    try {
      await deletePowScheduleItem(claimId, it.id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to delete item.");
    }
  }
  async function toggleComplete(it) {
    try {
      await updatePowScheduleItem(claimId, it.id, { description: it.description, is_complete: !it.is_complete });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to update item.");
    }
  }

  // Drag-and-drop: the server needs the FULL list, so drag is off while the
  // "Hide complete" filter (or an editor) is open.
  const dragDisabled = editId !== null || hideComplete;
  async function handleReorder(reordered) {
    const prev = items;
    setItems(reordered);
    try {
      await reorderList(claimId, "pow-schedule", reordered.map((l) => l.id));
    } catch (err) {
      setItems(prev);
      setError(err.response?.data?.error || "Unable to save the new order.");
    }
  }

  function editRow(key) {
    return (
      <tr key={key} className="bg-body-tertiary">
        <td /><td>
          <Form.Control as="textarea" rows={3} size="sm" ref={editRef} value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="Description" />
          <Form.Check className="mt-2" type="checkbox" id={`sched-done-${key}`} label="Is complete" checked={form.is_complete} onChange={(e) => set({ is_complete: e.target.checked })} />
        </td>
        <td className="text-end text-nowrap align-top" colSpan={2}>
          <Button size="sm" className="me-1" onClick={save}>{key === "new" ? "Add" : "Save"}</Button>
          <Button size="sm" variant="secondary" onClick={() => setEditId(null)}>Cancel</Button>
        </td>
      </tr>
    );
  }

  function viewRow(it) {
    return (
      <SortableTr key={it.id} id={it.id} disabled={dragDisabled} className={it.is_complete ? "text-muted" : undefined}>
        <td style={{ whiteSpace: "pre-wrap" }}>{it.description || <span className="text-muted">—</span>}</td>
        <td className="text-center text-nowrap">
          <Form.Check
            type="checkbox"
            className="d-inline-flex align-items-center gap-1 m-0"
            checked={it.is_complete}
            onChange={() => toggleComplete(it)}
            label={it.is_complete ? (it.completed_date ? formatDate(it.completed_date) : "Yes") : "No"}
            data-hint="Toggle complete"
          />
        </td>
        <td className="text-end text-nowrap">
          <Button variant="outline-primary" size="sm" className="me-1" onClick={() => startEdit(it)}>Edit</Button>
          <Button variant="outline-danger" size="sm" onClick={() => remove(it)}>Delete</Button>
        </td>
      </SortableTr>
    );
  }

  return (
    <div className="no-print">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Schedule</h5>
        <div className="d-flex align-items-center gap-3">
          <Form.Check type="checkbox" id="sched-hide-done" label="Hide complete" checked={hideComplete} onChange={changeHide} />
          {editId === null ? <Button size="sm" onClick={startNew}>+ New</Button> : null}
        </div>
      </div>

      {error ? <div className="alert alert-danger py-1 px-2 small">{error}</div> : null}

      <SortableRows items={items} onReorder={handleReorder} disabled={dragDisabled}>
      <Table striped bordered hover size="sm" className="mb-0 align-middle">
        <thead className="table-surface">
          <tr>
            <th style={{ width: 36 }} />
            <th>Description</th>
            <th className="text-center" style={{ width: 90 }}>Is Complete</th>
            <th className="text-end" style={{ width: 120 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (editId === it.id ? editRow(it.id) : viewRow(it)))}
          {editId === "new" ? editRow("new") : null}
          {items.length === 0 && editId !== "new" ? (
            <tr><td colSpan={4} className="text-center text-muted">No schedule items.</td></tr>
          ) : null}
        </tbody>
      </Table>
      </SortableRows>
    </div>
  );
}

export default ClaimPowSchedule;
